import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useState, useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TableFilterBar from "@/components/table/TableFilterBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Phone, Mail, Cake, ArrowLeft, MapPin, Building, Calendar, Users, User, Hash, Briefcase, GraduationCap, LayoutGrid, List, GripVertical, Plus, Trash2, Check } from "lucide-react";
import MemberNameCell from "@/components/MemberNameCell";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const MISSING_FIELDS = [
  { field: "phone", label: "Phone", icon: Phone },
  { field: "email", label: "Email", icon: Mail },
  { field: "date_of_birth", label: "Birthday", icon: Cake },
  { field: "gender", label: "Gender", icon: User },
  { field: "address", label: "Address", icon: MapPin },
  { field: "city", label: "City", icon: Building },
  { field: "state", label: "State", icon: Building },
  { field: "zip_code", label: "Zip Code", icon: Hash },
  { field: "date_joined", label: "Date Joined", icon: Calendar },
  { field: "department", label: "Ministry", icon: Users },
  { field: "education_status", label: "Education", icon: GraduationCap },
  { field: "employment_status", label: "Employment", icon: Briefcase },
] as const;

type MissingFieldKey = typeof MISSING_FIELDS[number]["field"];

const EDUCATION_STATUSES = ["Not in School", "Preschool", "Elementary School", "Middle School", "High School", "College / University", "Graduate School", "Vocational / Trade School", "Completed / Graduated"];
const EMPLOYMENT_STATUSES = ["Employed Full-Time", "Employed Part-Time", "Self-Employed", "Unemployed", "Retired"];

const getMemberMinistries = (department: string | null | undefined): string[] => {
  if (!department) return [];
  return department.split(",").map((value) => value.trim()).filter(Boolean);
};

export default function MissingInfoPage({ hideTitle, hideBackButton }: { hideTitle?: boolean; hideBackButton?: boolean } = {}) {
  const navigate = useNavigate();
  const { profile } = useChurch();
  const { role } = useAuth();
  const { getPeopleOrganizationIds, hasPeopleScopedAccess } = usePermissions();
  const churchId = profile?.id;
  const queryClient = useQueryClient();
  const allowedPeopleOrgIds = getPeopleOrganizationIds();
  const peopleScoped = role !== "admin" && hasPeopleScopedAccess();
  const [filter, setFilter] = useState<MissingFieldKey | "all">("all");
  const [search, setSearch] = useState("");
  const [ministryFilters, setMinistryFilters] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [summaryEditMode, setSummaryEditMode] = useState(false);
  const [summaryDraggedId, setSummaryDraggedId] = useState<string | null>(null);
  const [summaryDragHiddenId, setSummaryDragHiddenId] = useState<string | null>(null);
  const [summaryLastDragOverId, setSummaryLastDragOverId] = useState<string | null>(null);
  const [visibleSummaryIds, setVisibleSummaryIds] = useState<string[]>([]);
  const [hiddenSummaryIds, setHiddenSummaryIds] = useState<string[]>([]);
  const summaryStorageKey = `missing-info-summary-layout:${churchId || "default"}`;

  const { data: members = [] } = useQuery({
    queryKey: ["missing_info_members", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, phone, email, date_of_birth, gender, address, city, state, zip_code, date_joined, department, education_status, employment_status")
        .eq("church_id", churchId!)
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "missing_info_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const allowedMinistryNames = useMemo(() => {
    if (!peopleScoped) return null;
    const names = organizations
      .filter((org: any) => allowedPeopleOrgIds.includes(org.id))
      .map((org: any) => org.name)
      .filter(Boolean);
    return new Set(names);
  }, [peopleScoped, organizations, allowedPeopleOrgIds]);

  const scopedMembers = useMemo(() => {
    if (!peopleScoped || !allowedMinistryNames) return members;
    return members.filter((member: any) => {
      const ministries = getMemberMinistries(member.department);
      return ministries.some((name) => allowedMinistryNames.has(name));
    });
  }, [members, peopleScoped, allowedMinistryNames]);

  const allDepartments = [...organizations].map((o: any) => o.name).sort((a, b) => a.localeCompare(b));
  const ministryOptions = useMemo(() => {
    return Array.from(
      new Set(
        scopedMembers
          .flatMap((m: any) => getMemberMinistries(m.department))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [scopedMembers]);

  const missingFieldOptions = useMemo(() => {
    return MISSING_FIELDS.map((f) => f.label);
  }, []);

  const selectedMissingField = useMemo(
    () => (filter === "all" ? new Set<string>() : new Set<string>([MISSING_FIELDS.find((f) => f.field === filter)?.label || ""])),
    [filter]
  );

  const toggleMissingFieldFilter = (label: string) => {
    const field = MISSING_FIELDS.find((f) => f.label === label)?.field;
    if (!field) return;
    setFilter((prev) => (prev === field ? "all" : field));
  };

  const toggleMinistryFilter = (val: string) => {
    setMinistryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const filteredMembers = useMemo(() => {
    return scopedMembers.filter((m: any) => {
      const hasMissing =
        filter === "all"
          ? MISSING_FIELDS.some((f) => !m[f.field])
          : !m[filter];
      const memberMinistries = getMemberMinistries(m.department);
      const matchesMinistry = ministryFilters.size === 0 || memberMinistries.some((ministry) => ministryFilters.has(ministry));
      const matchesSearch = m.full_name.toLowerCase().includes(search.toLowerCase());
      return hasMissing && matchesSearch && matchesMinistry;
    });
  }, [scopedMembers, filter, search, ministryFilters]);

  const updateMutation = useMutation({
    mutationFn: async ({ id, field, value }: { id: string; field: string; value: string }) => {
      const { error } = await supabase.from("members").update({ [field]: value || null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["missing_info_members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_all_members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_member_count"] });
      queryClient.invalidateQueries({ queryKey: ["header_notifications"] });
      
      setEditingId(null);
      setEditValues({});
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const getMissingFields = (m: any) => {
    return MISSING_FIELDS.filter((f) => !m[f.field]);
  };

  const stats = useMemo(() => {
    return MISSING_FIELDS.map((f) => ({
      ...f,
      count: scopedMembers.filter((m: any) => !m[f.field]).length,
    })).filter((f) => f.count > 0);
  }, [scopedMembers]);

  const summaryById = useMemo(
    () => new Map(stats.map((s) => [s.field, s] as const)),
    [stats]
  );
  const allSummaryIds = useMemo(() => stats.map((s) => s.field), [stats]);

  useEffect(() => {
    if (visibleSummaryIds.length === 0 && allSummaryIds.length > 0) {
      const raw = localStorage.getItem(summaryStorageKey);
      if (!raw) {
        setVisibleSummaryIds(allSummaryIds);
        setHiddenSummaryIds([]);
        return;
      }
      try {
        const parsed = JSON.parse(raw) as { visibleSummaryIds?: string[]; hiddenSummaryIds?: string[] };
        const visible = (parsed.visibleSummaryIds || []).filter((id) => allSummaryIds.includes(id as MissingFieldKey));
        const hidden = (parsed.hiddenSummaryIds || []).filter((id) => allSummaryIds.includes(id as MissingFieldKey) && !visible.includes(id));
        const missing = allSummaryIds.filter((id) => !visible.includes(id) && !hidden.includes(id));
        setVisibleSummaryIds(visible.length > 0 ? visible : allSummaryIds);
        setHiddenSummaryIds([...hidden, ...missing]);
      } catch {
        setVisibleSummaryIds(allSummaryIds);
        setHiddenSummaryIds([]);
      }
      return;
    }

    if (allSummaryIds.length > 0 && visibleSummaryIds.length > 0) {
      const nextVisible = visibleSummaryIds.filter((id) => allSummaryIds.includes(id as MissingFieldKey));
      const nextHidden = hiddenSummaryIds.filter((id) => allSummaryIds.includes(id as MissingFieldKey) && !nextVisible.includes(id));
      const missing = allSummaryIds.filter((id) => !nextVisible.includes(id) && !nextHidden.includes(id));
      if (missing.length > 0 || nextVisible.length !== visibleSummaryIds.length || nextHidden.length !== hiddenSummaryIds.length) {
        setVisibleSummaryIds(nextVisible.length > 0 ? nextVisible : allSummaryIds);
        setHiddenSummaryIds([...nextHidden, ...missing]);
      }
    }
  }, [allSummaryIds, visibleSummaryIds, hiddenSummaryIds, summaryStorageKey]);

  useEffect(() => {
    if (allSummaryIds.length === 0 || visibleSummaryIds.length === 0) return;
    localStorage.setItem(summaryStorageKey, JSON.stringify({ visibleSummaryIds, hiddenSummaryIds }));
  }, [summaryStorageKey, allSummaryIds.length, visibleSummaryIds, hiddenSummaryIds]);

  const visibleSummaryCards = useMemo(
    () => visibleSummaryIds.map((id) => summaryById.get(id as MissingFieldKey)).filter(Boolean),
    [visibleSummaryIds, summaryById]
  );

  const hiddenSummaryCards = useMemo(
    () => hiddenSummaryIds.map((id) => summaryById.get(id as MissingFieldKey)).filter(Boolean),
    [hiddenSummaryIds, summaryById]
  );

  const moveSummaryCard = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    setVisibleSummaryIds((prev) => {
      const from = prev.indexOf(fromId);
      const to = prev.indexOf(toId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, fromId);
      return next;
    });
  };

  const removeSummaryCard = (id: string) => {
    setVisibleSummaryIds((prev) => prev.filter((cardId) => cardId !== id));
    setHiddenSummaryIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const addSummaryCard = (id: string) => {
    setHiddenSummaryIds((prev) => prev.filter((cardId) => cardId !== id));
    setVisibleSummaryIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const missingInfoTableBaseRows = 10;
  const missingInfoEmptyRows = Math.max(0, missingInfoTableBaseRows - filteredMembers.length);

  const renderEditInput = (field: string, label: string) => {
    if (field === "gender") {
      return (
        <Select value={editValues[field] || ""} onValueChange={(v) => setEditValues((prev) => ({ ...prev, [field]: v }))}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select gender" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (field === "department") {
      return (
        <Select value={editValues[field] || ""} onValueChange={(v) => setEditValues((prev) => ({ ...prev, [field]: v }))}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select ministry" /></SelectTrigger>
          <SelectContent>
            {allDepartments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (field === "education_status") {
      return (
        <Select value={editValues[field] || ""} onValueChange={(v) => setEditValues((prev) => ({ ...prev, [field]: v }))}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select status" /></SelectTrigger>
          <SelectContent>
            {EDUCATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (field === "employment_status") {
      return (
        <Select value={editValues[field] || ""} onValueChange={(v) => setEditValues((prev) => ({ ...prev, [field]: v }))}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select status" /></SelectTrigger>
          <SelectContent>
            {EMPLOYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    const type = field === "date_of_birth" || field === "date_joined" ? "date" : field === "email" ? "email" : "text";
    return (
      <Input
        type={type}
        placeholder={label}
        value={editValues[field] || ""}
        onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
        className="h-8 text-sm"
      />
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {(!hideBackButton || !hideTitle) && (
        <div className="flex items-center gap-3">
          {!hideBackButton && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {!hideTitle && (
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Missing Information</h1>
              <p className="text-muted-foreground text-sm">Review and fix incomplete member records</p>
            </div>
          )}
        </div>
      )}

      {/* Summary stats */}
      <div className="space-y-2" onDoubleClick={() => { if (summaryEditMode) setSummaryEditMode(false); }}>
      <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {visibleSummaryCards.map((s) => {
          if (!s) return null;
          const Icon = s.icon;
          return (
            <Card
              key={s.field}
              className={cn(
                "group relative cursor-pointer hover:shadow-md transition-shadow border-slate-100",
                filter === s.field && "ring-2 ring-destructive/40",
                summaryEditMode && "cursor-grab active:cursor-grabbing ring-1 ring-primary/25",
                summaryDragHiddenId === s.field && "opacity-0"
              )}
              onClick={() => {
                if (summaryEditMode) return;
                setFilter(filter === s.field ? "all" : s.field);
              }}
              draggable={summaryEditMode}
              onDragStart={() => {
                if (!summaryEditMode) return;
                setSummaryDraggedId(s.field);
                setSummaryLastDragOverId(null);
                requestAnimationFrame(() => setSummaryDragHiddenId(s.field));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!summaryDraggedId || summaryDraggedId === s.field) return;
                if (summaryLastDragOverId === s.field) return;
                moveSummaryCard(summaryDraggedId, s.field);
                setSummaryLastDragOverId(s.field);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setSummaryLastDragOverId(null);
              }}
              onDragLeave={() => {
                if (summaryLastDragOverId === s.field) setSummaryLastDragOverId(null);
              }}
              onDragEnd={() => {
                setSummaryDraggedId(null);
                setSummaryDragHiddenId(null);
                setSummaryLastDragOverId(null);
              }}
            >
              {summaryEditMode && (
                <button
                  type="button"
                  className="absolute right-7 top-1 z-10 p-1 rounded-md bg-background/90 border border-border transition-colors hover:bg-primary/10"
                  aria-label="Done editing cards"
                  onClick={() => setSummaryEditMode(false)}
                >
                  <Check className="w-3.5 h-3.5 text-primary" />
                </button>
              )}
              <button
                type="button"
                className={cn(
                  "absolute right-1 top-1 z-10 p-1 transition-opacity cursor-grab active:cursor-grabbing",
                  summaryEditMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                )}
                aria-label="Drag to rearrange"
                onClick={() => setSummaryEditMode(true)}
              >
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {summaryEditMode && visibleSummaryIds.length > 1 && (
                <button
                  type="button"
                  className="absolute left-1 top-1 z-10 p-1 rounded-md bg-background/90 border border-border hover:bg-destructive/10"
                  onClick={() => removeSummaryCard(s.field)}
                  aria-label={`Remove ${s.label}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </button>
              )}
              <CardContent className="pt-3 pb-3 px-3 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-destructive" />
                  </div>
                  <p className="text-base font-bold text-foreground leading-tight">{s.count}</p>
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">Missing {s.label}</p>
              </CardContent>
            </Card>
          );
        })}
        {summaryEditMode && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors min-h-[92px] flex flex-col items-center justify-center gap-1"
              >
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {hiddenSummaryCards.length === 0 ? (
                <DropdownMenuItem disabled className="text-[11px]">All cards are already shown</DropdownMenuItem>
              ) : (
                hiddenSummaryCards.map((hiddenCard) => (
                  <DropdownMenuItem key={hiddenCard!.field} onClick={() => addSummaryCard(hiddenCard!.field)} className="text-[11px]">
                    Missing {hiddenCard!.label}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      </div>

      <div className="stat-card p-0 overflow-visible border-transparent">
        <div className="px-0 py-3 border-b border-transparent relative sm:px-4">
          <TableFilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterColumns={2}
            filterGroups={[
              {
                key: "missing_field",
                label: "Missing Field",
                options: missingFieldOptions,
                selected: selectedMissingField,
                onToggle: toggleMissingFieldFilter,
                onClear: () => setFilter("all"),
              },
              {
                key: "ministry",
                label: "Ministry",
                options: ministryOptions,
                selected: ministryFilters,
                onToggle: toggleMinistryFilter,
                onClear: () => setMinistryFilters(new Set()),
              },
            ]}
            leadingControls={
              <div className="flex items-center border border-border rounded-md overflow-hidden sm:rounded-lg">
                <button
                  type="button"
                  onClick={() => setViewMode("cards")}
                  className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={cn("p-2 transition-colors", viewMode === "table" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
                  title="Table view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            }
          />
        </div>

        {/* Member list */}
        <div className="min-h-[420px]">
        {viewMode === "cards" ? (
          filteredMembers.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No members with missing information found</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((m: any) => {
            const missing = getMissingFields(m);
            const isEditing = editingId === m.id;
            return (
              <Card
                key={m.id}
                className={cn("p-4", !isEditing && "cursor-pointer transition-colors hover:bg-muted/20")}
                onClick={() => {
                  if (isEditing) return;
                  setEditingId(m.id);
                  setEditValues({});
                }}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <MemberNameCell name={m.full_name} iconStyle="avatar" />
                  <div className="hidden sm:flex flex-wrap gap-1 justify-end">
                    {missing.slice(0, 3).map((f) => {
                      const Icon = f.icon;
                      return (
                        <Badge key={f.field} variant="outline" className="text-[10px] text-destructive border-destructive/30 flex items-center gap-1">
                          <Icon className="w-3 h-3" /> {f.label}
                        </Badge>
                      );
                    })}
                    {missing.length > 3 && (
                      <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">+{missing.length - 3}</Badge>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-2 mt-2">
                    <div className="grid grid-cols-1 gap-2">
                      {missing.map((f) => (
                        <div key={f.field} className="space-y-1">
                          <label className="text-xs text-muted-foreground">{f.label}</label>
                          {renderEditInput(f.field, f.label)}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-8 text-xs" disabled={updateMutation.isPending}
                        onClick={() => {
                          const entries = Object.entries(editValues).filter(([_, v]) => v.trim());
                          if (entries.length === 0) { toast.error("Fill in at least one field"); return; }
                          entries.forEach(([field, value]) => { updateMutation.mutate({ id: m.id, field, value }); });
                        }}
                      >Save</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setEditingId(null); setEditValues({}); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex items-center gap-1 sm:hidden">
                      {missing.slice(0, 3).map((f) => {
                        const Icon = f.icon;
                        return (
                          <span key={f.field} className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-destructive/30 text-destructive bg-destructive/5">
                            <Icon className="h-3 w-3" />
                          </span>
                        );
                      })}
                      {missing.length > 3 && (
                        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-destructive/30 px-1 text-[10px] font-medium text-destructive bg-destructive/5">
                          +{missing.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
          </div>
          )
        ) : (
          <div className="relative w-full -mx-4 rounded-none overflow-hidden sm:mx-0">
            <div className="divide-y divide-border px-4">
              {filteredMembers.length === 0 ? (
                <div className="py-3 text-center text-sm text-muted-foreground">
                  No members with missing information found
                </div>
              ) : filteredMembers.map((m: any) => {
                const missing = getMissingFields(m);
                const isEditing = editingId === m.id;
                return (
                  <div
                    key={m.id}
                    className={cn("py-3", !isEditing && "cursor-pointer transition-colors hover:bg-muted/20")}
                    onClick={() => {
                      if (isEditing) return;
                      setEditingId(m.id);
                      setEditValues({});
                    }}
                  >
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <MemberNameCell name={m.full_name} iconStyle="avatar" />
                      <div className="hidden sm:flex flex-wrap gap-1 justify-end">
                        {missing.slice(0, 4).map((f) => {
                          const Icon = f.icon;
                          return (
                            <Badge key={f.field} variant="outline" className="text-[10px] text-destructive border-destructive/30 flex items-center gap-1">
                              <Icon className="w-3 h-3" /> {f.label}
                            </Badge>
                          );
                        })}
                        {missing.length > 4 && (
                          <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">
                            +{missing.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="space-y-2 mt-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {missing.map((f) => (
                            <div key={f.field} className="space-y-1">
                              <label className="text-xs text-muted-foreground">{f.label}</label>
                              {renderEditInput(f.field, f.label)}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="h-8 text-xs"
                            disabled={updateMutation.isPending}
                            onClick={() => {
                              const entries = Object.entries(editValues).filter(([_, v]) => v.trim());
                              if (entries.length === 0) { toast.error("Fill in at least one field"); return; }
                              entries.forEach(([field, value]) => {
                                updateMutation.mutate({ id: m.id, field, value });
                              });
                            }}
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setEditingId(null); setEditValues({}); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex items-center gap-1 sm:hidden">
                          {missing.slice(0, 4).map((f) => {
                            const Icon = f.icon;
                            return (
                              <span key={f.field} className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-destructive/30 text-destructive bg-destructive/5">
                                <Icon className="h-3 w-3" />
                              </span>
                            );
                          })}
                          {missing.length > 4 && (
                            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-destructive/30 px-1 text-[10px] font-medium text-destructive bg-destructive/5">
                              +{missing.length - 4}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {Array.from({ length: missingInfoEmptyRows }).map((_, idx) => (
                <div key={`missing-info-empty-row-${idx}`} className="py-3" />
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
